/**
 * Topics of the guide for employees (in-app help and the printed A4 PDF) in German, English and Turkish;
 * the topics for leads, office and administration are in help-lead.ts, the head of each tab and the newer
 * topics in help-topics.ts. Which topic is shown in which tab: GUIDE_TOPICS in @pz/core.
 * Keep the three languages in the same structure; `**text**` is printed bold, `{{tol}}` is replaced by
 * the weekly tolerance in hours from the settings.
 */

export interface HelpStep {
  title: string;
  text: string;
  who?: string;
}

export interface HelpText {
  print: string;
  privacy: { title: string; text: string };
  s1: { title: string; lead: string; steps: [HelpStep, HelpStep, HelpStep, HelpStep, HelpStep] };
  s2: { title: string; lead: string; steps: [HelpStep, HelpStep, HelpStep, HelpStep]; billing: string; submit: string; undo: string };
  s3: {
    title: string;
    q1: [string, string];
    a1: [string, string];
    q2: [string, string];
    a2: [string, string];
    a3: [string, string];
    yes: string;
    no: string;
    aria: string;
  };
  s4: {
    title: string;
    lead: string;
    draft: [string, string];
    submitted: [string, string];
    l1: [string, string];
    approved: [string, string];
    billed: [string, string];
    rejected: [string, string];
    arrows: { submit: string; lead: string; office: string; close: string; fix: string };
    aria: string;
  };
  s5: { title: string; days: { d: string; t: string; s: string }[]; weekend: string };
  /**
   * sickDoc: sentence about the sick note, added when the company expects one ({{docDay}} = calendar day);
   * change: what happens when the approver asks for a change of a request
   */
  s6: { title: string; lead: string; items: [{ title: string; text: string }, { title: string; text: string }, { title: string; text: string }]; sickDoc: string; change: string };
  s7: { title: string; lead: string; bad: [string, string]; warn: [string, string]; good: [string, string]; note: string };
  s8: {
    title: string;
    lead: string;
    example: { title: string; plan: string; booked: string; result: string };
    points: [string, string, string, string];
  };
  s11: { title: string; under: string; over: string; fast: string; forma: string; location: string; hatch: string };
  s12: { title: string; items: [{ title: string; text: string }, { title: string; text: string }, { title: string; text: string }] };
}

export const HELP: Record<'de' | 'en' | 'tr', HelpText> = {
  de: {
    print: 'Drucken / PDF',
    privacy: {
      title: 'Wer sieht meine Stunden?',
      text: 'Entwürfe sehen nur Sie. Eingereichte Einträge sehen Sie selbst, die Projektleitung des jeweiligen Projekts, das Büro (Freigabe) und die Geschäftsführung. Kolleginnen und Kollegen sehen weder Ihre Stunden noch Ihre Sollzeit; im Teamkalender sehen sie nur, wann Sie abwesend sind, bei Krankheit ohne Grund. Bescheinigungen zu Krankheit sieht nur die Personalverwaltung. Persönliche Kennzahlen sieht nur die Geschäftsführung; jeder Aufruf wird protokolliert.',
    },
    s1: {
      title: 'Auf einen Blick',
      lead: 'Aufgaben kommen aus Forma, Stunden entstehen in Projektzeit. Forma wird nie verändert.',
      steps: [
        { title: 'Forma', text: 'Issues, Kategorien, Standorte und gewählte Felder: alle 15 Minuten gelesen, täglich 6-24 Uhr.', who: 'automatisch' },
        { title: 'Buchen', text: 'Stunden auf ein Issue oder eine manuelle Aufgabe; mehrere Personen je Issue.', who: 'alle' },
        { title: 'Freigeben', text: 'Je nach Projekt: Projektleitung, Büro oder beide nacheinander.', who: 'PL, Büro' },
        { title: 'Planen', text: 'Plan-Stunden je Aufgabe, Personal je Woche.', who: 'PL, Leitung' },
        { title: 'Auswerten, abrechnen', text: 'Cockpit, Projektanalyse, Stundennachweis, Abschlag.', who: 'Leitung, Abrechnung' },
      ],
    },
    s2: {
      title: 'Zeit erfassen in vier Schritten',
      lead: 'Knopf „Zeit erfassen“ auf der Übersicht, in Meine Woche oder bei jeder Aufgabe.',
      steps: [
        { title: '1. Öffnen', text: '„Zeit erfassen“ oder in Meine Woche auf einen Tag klicken.' },
        { title: '2. Filtern', text: 'Quelle (Forma / Eigene), Projekt, Kategorie und Typ aus Forma, Standort.' },
        { title: '3. Aufgabe finden', text: 'Nummer (#12, M-3) oder Titel eingeben; zuletzt genutzte stehen oben.' },
        { title: '4. Eintragen', text: 'Stunden (7,5 oder 7:30), Tätigkeit, Arbeitsort und Abrechnungsart.' },
      ],
      billing: '**Abrechnungsart**: Die Vorgabe kommt aus Aufgabe und Projekt. Arbeit, die dem Kunden nicht berechnet wird (z. B. interne Abstimmung zur Aufgabe, eigene Weiterentwicklung wie ein Dynamo-Skript), stellen Sie auf „Nicht abrechnen“; eine feste Regel (z. B. interne Aufgaben) ist nicht änderbar. Die abzurechnenden Stunden legt die Abrechnung fest. Die Tätigkeit dürfen Sie in Ihrer Sprache schreiben: im Stundennachweis wird sie ein Satz hinter Ihrem Kürzel (so ist es voreingestellt), ohne Tätigkeit steht dort der Aufgabentitel; den Text für den Kunden prüft das Büro.',
      submit: 'Am Freitag: **Woche einreichen**. Bis dahin bleiben Einträge Entwürfe und lassen sich frei ändern. Unter **Meine Zeiten** sehen Sie alle Einträge (Diese Woche, Dieser Monat …); **Excel** lädt sie samt Ihrer Beschreibung herunter, auch als eigene Sicherung.',
      undo: 'Aus Versehen eingereicht oder gelöscht? **Rückgängig** in der Meldung unten rechts, oder in Meine Woche **Woche zurückziehen**, solange noch nichts freigegeben ist. Jeder Schritt steht in der Historie des Eintrags.',
    },
    s3: {
      title: 'Wo buche ich das?',
      q1: ['Gibt es ein Issue in Forma?', 'z. B. #12 Heizlastberechnung'],
      a1: ['Auf das Forma-Issue buchen', 'Quelle „Forma“, Projekt und Standort wählen'],
      q2: ['Gehört die Arbeit zu einem Projekt?', 'Besprechung, Baustelle, Nachtrag …'],
      a2: ['Manuelle Aufgabe im Projekt (M-Nummer)', 'Kategorie wählen, z. B. Besprechung und Abstimmung'],
      a3: ['Interne Aufgabe im Projekt „Intern“', 'Weiterbildung, F&E, Verwaltung … (nie abrechenbar)'],
      yes: 'ja',
      no: 'nein',
      aria: 'Entscheidung: Gibt es ein Forma-Issue, darauf buchen. Sonst Projektarbeit als manuelle Aufgabe im Projekt, sonst interne Aufgabe.',
    },
    s4: {
      title: 'Der Weg eines Zeiteintrags',
      lead: 'Nach der Freigabe ändern nur Projektleitung oder Büro, mit Begründung im Protokoll.',
      draft: ['Entwurf', 'noch frei änderbar'],
      submitted: ['Eingereicht', 'wartet auf Prüfung'],
      l1: ['Projektleitung ok', 'Stufe 1 erledigt'],
      approved: ['Freigegeben', 'Stufe 2 (Büro)'],
      billed: ['Abgerechnet', 'im Stundennachweis'],
      rejected: ['Zurückgewiesen', 'mit Begründung, bitte korrigieren'],
      arrows: { submit: 'Einreichen', lead: 'PL prüft', office: 'Büro', close: 'Abrechnung', fix: 'korrigieren und neu einreichen' },
      aria: 'Statusweg eines Zeiteintrags: Entwurf, Eingereicht, Projektleitung ok, Freigegeben, Abgerechnet; eine Zurückweisung führt zur Korrektur.',
    },
    s5: {
      title: 'Wochenrhythmus',
      days: [
        { d: 'Mo bis Do', t: 'Täglich buchen', s: 'am besten am Ende des Tages' },
        { d: 'Freitag', t: 'Woche einreichen', s: '„Woche einreichen“ in Meine Woche' },
        { d: 'Mo / Di', t: 'Projektleitung prüft', s: 'Freigaben, Stufe 1' },
        { d: 'bis Mi', t: 'Büro gibt frei', s: 'Freigaben, Stufe 2' },
        { d: 'Monatsende', t: 'Monatsabschluss', s: 'danach Abrechnung' },
      ],
      weekend: '**Wochenende** ist normalerweise frei. Wer doch arbeitet (z. B. vor einer Abgabe), blendet in Meine Woche das Wochenende ein und bucht dort; auch Stunden an Feiertagen, an Abwesenheitstagen und über dem Soll zählen als Überstunden. Sie werden als **Freizeitausgleich** genommen oder mit dem Lohn **ausgezahlt**; Saldo und Auszahlungen stehen unter Meine Abwesenheiten.',
    },
    s6: {
      title: 'Abwesenheit, Team und Feiertage',
      lead: 'Beantragen, Nachweis anhängen, entscheiden lassen und im Kalender sehen.',
      items: [
        {
          title: 'Urlaub und andere Abwesenheit',
          text: 'Unter **Meine Abwesenheiten** beantragen, auch halbe Tage (vormittags oder nachmittags) oder Stunden; dort stehen auch Anspruch, Rest und Überstunden. Jeder Antrag wird genehmigt oder mit Begründung abgelehnt, von Ihrer genehmigenden Person (z. B. Teamleitung) oder der Personalverwaltung. Erst genehmigte Tage zählen und erscheinen in Outlook. Nachweise (z. B. Einladung zur Weiterbildung) lassen sich anhängen. Zurückziehen geht bis zum Beginn.',
        },
        {
          title: 'Krankheit',
          text: 'Über **Krankmeldung** eintragen: zählt sofort für Sollzeit und Kalender, die Personalverwaltung bestätigt sie. Die Art sehen nur Sie und die Personalverwaltung, alle anderen sehen „abwesend“.',
        },
        {
          title: 'Team und Feiertage',
          text: 'Unter **Team** sieht jede Person, wer wann da ist, die Feiertage jedes Standorts, Firmentermine und wer an welchen Projekten arbeitet, ohne Stunden und Aufgaben. Geburtstage nur, wenn die Person es im Profil einschaltet.',
        },
      ],
      sickDoc: 'Ab dem **{{docDay}}. Kalendertag** die Bescheinigung als PDF oder Foto hochladen, gleich in der Krankmeldung oder später unter Meine Abwesenheiten.',
      change: '**Änderung gewünscht?** Lässt sich ein Antrag so nicht genehmigen, bittet die genehmigende Person mit einem Hinweis um eine **Änderung**: die Tage sind wieder frei, und Sie wählen unter Meine Abwesenheiten **Anpassen und erneut senden**; der Antrag ist vorausgefüllt.',
    },
    s7: {
      title: 'Hinweise auf der Übersicht',
      lead: 'Drei Farben, drei Bedeutungen.',
      bad: ['Rot: bitte handeln', 'Zurückgewiesene Einträge korrigieren, abgelehnte Anträge ansehen, fehlende Bescheinigung hochladen. Woche deutlich unter Ihrer Sollzeit: fehlen Buchungen oder eine Abwesenheit?'],
      warn: ['Gelb: bitte prüfen', 'Einträge der letzten Woche noch nicht eingereicht. Woche deutlich über Ihrer Sollzeit (Überstunden). Für Genehmigende: offene Abwesenheitsanträge.'],
      good: ['Grün: gut gemacht', 'Aufgabe deutlich schneller als geplant erledigt (z. B. Plan 10 h, gebucht 4 h) oder Antrag genehmigt.'],
      note: 'Hinweis ab {{tol}} h Abweichung je Woche. Ihre Sollzeit sehen nur Sie, die Personalverwaltung und die Geschäftsführung.',
    },
    s8: {
      title: 'Plan, Ist und Plan-Ist-Quote',
      lead: 'Wer eine Aufgabe schneller als geplant erledigt, sieht das deutlich.',
      example: { title: 'Beispiel: #6 Trinkwasser Strangschema', plan: 'Plan', booked: 'Gebucht', result: 'Plan-Ist-Quote 10 h / 4 h = 250 %' },
      points: [
        '**Plan** trägt die Projektleitung unter **Planung** ein (nur in Projektzeit, nicht in Forma). Plan 0 = Aufgabe wird nicht bewertet.',
        'Gezählt wird, wenn die Aufgabe **erledigt** ist. In Ihren eigenen Kennzahlen stehen nur Aufgaben, auf die nur Sie gebucht haben; gemeinsam bearbeitete Aufgaben wertet nur die Geschäftsführung aus, weil die Quote sonst die Stunden der anderen verraten würde.',
        'Grundlage sind immer die **erfassten** Stunden. Abzurechnende Stunden (Rechnung an den Kunden) ändern die Quote nicht.',
        '**Meine Kennzahlen** zeigen auch, wofür Ihre Zeit verwendet wurde: nach Projekt und nach Bereich (Projektarbeit, Weiterbildung, F&E …). Nur für Sie sichtbar.',
      ],
    },
    s11: {
      title: 'Symbole',
      under: 'Woche unter Soll: mehr als {{tol}} h weniger gebucht als Ihre Sollzeit.',
      over: 'Woche über Soll: mehr als {{tol}} h Überstunden.',
      fast: 'Schneller als geplant.',
      forma: 'In Forma gelöscht oder nicht mehr sichtbar: Buchungen bleiben gültig, die Aufgabe ist markiert. Wird das Issue in Forma wiederhergestellt, verschwindet die Markierung beim nächsten Abgleich.',
      location: 'Standort aus Forma, z. B. „Bettenhaus / 2. OG“.',
      hatch: 'Schraffur: Feiertag, Abwesenheit oder Freizeitausgleich.',
    },
    s12: {
      title: 'Hilfe',
      items: [
        { title: 'Zugang oder Passwort vergessen', text: 'Die Verwaltung sendet einen neuen Einrichtungslink.' },
        { title: 'Etwas falsch gemacht', text: 'Rückgängig in der Meldung. Einträge sind bis zur Freigabe frei änderbar; die Historie zeigt jeden Schritt.' },
        { title: 'Falsche Aufgabe gebucht', text: 'Eintrag bearbeiten und „Andere Aufgabe“ wählen, solange er nicht freigegeben ist.' },
      ],
    },
  },

  en: {
    print: 'Print / PDF',
    privacy: {
      title: 'Who sees my hours?',
      text: 'Drafts are visible to you only. Submitted entries are visible to you, the project lead of the project concerned, the office (approval) and management. Colleagues see neither your hours nor your target hours; in the team calendar they only see when you are away, without the reason when you are sick. Certificates for sickness are visible to HR only. Personal figures are visible to management only; every access is logged.',
    },
    s1: {
      title: 'At a glance',
      lead: 'Tasks come from Forma, hours are recorded in Projektzeit. Forma is never changed.',
      steps: [
        { title: 'Forma', text: 'Issues, categories, locations and chosen fields: read every 15 minutes, daily 6 am to midnight.', who: 'automatic' },
        { title: 'Book', text: 'Hours on an issue or a manual task; several people per issue.', who: 'everyone' },
        { title: 'Approve', text: 'Depending on the project: project lead, office or both in turn.', who: 'PL, office' },
        { title: 'Plan', text: 'Planned hours per task, staff per week.', who: 'PL, management' },
        { title: 'Analyse, invoice', text: 'Cockpit, project analysis, timesheet, progress invoice.', who: 'management, billing' },
      ],
    },
    s2: {
      title: 'Record time in four steps',
      lead: 'Button "Record time" on the overview, in My week or on any task.',
      steps: [
        { title: '1. Open', text: '"Record time" or click a day in My week.' },
        { title: '2. Filter', text: 'Source (Forma / own), project, Forma category and type, location.' },
        { title: '3. Find the task', text: 'Type the number (#12, M-3) or the title; recently used tasks come first.' },
        { title: '4. Enter', text: 'Hours (7.5 or 7:30), description, work location and billing type.' },
      ],
      billing: '**Billing type**: the default comes from task and project. Work that is not charged to the client (e.g. an internal meeting on the task, own development such as a Dynamo script) you set to "Do not bill"; a fixed rule (e.g. internal tasks) cannot be changed. The hours to invoice are set by billing staff. You may write the description in your language: in the timesheet it becomes a sentence after your short code (the default), without a description the task title stands there; the office checks the text for the client.',
      submit: 'On Friday: **Submit week**. Until then entries stay drafts and can be changed freely. **My hours** lists all your entries (This week, This month …); **Excel** downloads them with your description, also as your own backup.',
      undo: 'Submitted or deleted by mistake? **Undo** in the message at the bottom right, or **Withdraw week** in My week as long as nothing has been approved. Every step is shown in the history of the entry.',
    },
    s3: {
      title: 'Where do I book this?',
      q1: ['Is there an issue in Forma?', 'e.g. #12 heating load calculation'],
      a1: ['Book on the Forma issue', 'choose source "Forma", project and location'],
      q2: ['Does the work belong to a project?', 'meeting, site visit, change order …'],
      a2: ['Manual task in the project (M number)', 'choose a category, e.g. meetings and coordination'],
      a3: ['Internal task in the project "Intern"', 'training, R&D, administration … (never billable)'],
      yes: 'yes',
      no: 'no',
      aria: 'Decision: if there is a Forma issue, book on it. Otherwise project work as a manual task in the project, otherwise an internal task.',
    },
    s4: {
      title: 'The path of a time entry',
      lead: 'After approval only the project lead or the office can change it, with a reason in the log.',
      draft: ['Draft', 'can still be changed'],
      submitted: ['Submitted', 'waiting for review'],
      l1: ['Project lead ok', 'stage 1 done'],
      approved: ['Approved', 'stage 2 (office)'],
      billed: ['Invoiced', 'in the timesheet'],
      rejected: ['Rejected', 'with a reason, please correct'],
      arrows: { submit: 'Submit', lead: 'PL reviews', office: 'Office', close: 'Invoicing', fix: 'correct and submit again' },
      aria: 'Status path of a time entry: draft, submitted, project lead ok, approved, invoiced; a rejection leads to a correction.',
    },
    s5: {
      title: 'Weekly rhythm',
      days: [
        { d: 'Mon to Thu', t: 'Book daily', s: 'ideally at the end of the day' },
        { d: 'Friday', t: 'Submit week', s: '"Submit week" in My week' },
        { d: 'Mon / Tue', t: 'Project lead reviews', s: 'Approvals, stage 1' },
        { d: 'by Wed', t: 'Office approves', s: 'Approvals, stage 2' },
        { d: 'Month end', t: 'Month closing', s: 'then invoicing' },
      ],
      weekend: '**Weekends** are normally off. If you do work (e.g. before a deadline), show the weekend in My week and book there; hours on public holidays, on absence days and above the target count as extra hours too. They are taken as **time off in lieu** or **paid out** with the salary; balance and payouts are shown under My absences.',
    },
    s6: {
      title: 'Absence, team and public holidays',
      lead: 'Request, attach a document, have it decided and see it in the calendar.',
      items: [
        {
          title: 'Vacation and other absence',
          text: 'Request it under **My absences**, also half days (morning or afternoon) or hours; your entitlement, the rest and your extra hours are shown there too. Every request is approved or rejected with a reason, by your approver (e.g. the team lead) or by HR. Only approved days count and appear in Outlook. Documents (e.g. the invitation to a training) can be attached. You can withdraw until the absence starts.',
        },
        {
          title: 'Sickness',
          text: 'Use **Report sickness**: it counts at once for target hours and calendar, HR confirms it. The kind is visible only to you and HR, everybody else sees "absent".',
        },
        {
          title: 'Team and public holidays',
          text: 'Under **Team** everybody sees who is there when, the public holidays of each location, company events and who works on which projects, without hours and tasks. Birthdays only when the person switches it on in the profile.',
        },
      ],
      sickDoc: 'From **calendar day {{docDay}}** upload the certificate as PDF or photo, right in the sick report or later under My absences.',
      change: '**Change wanted?** If a request cannot be approved as it is, the approver asks for a **change** with a note: the days are free again and you choose **Adjust and resubmit** under My absences; the request is prefilled.',
    },
    s7: {
      title: 'Notices on the overview',
      lead: 'Three colours, three meanings.',
      bad: ['Red: please act', 'Correct rejected entries, look at rejected requests, upload a missing certificate. Week clearly below your target hours: are bookings or an absence missing?'],
      warn: ['Amber: please check', 'Entries of last week not yet submitted. Week clearly above your target hours (extra hours). For approvers: open absence requests.'],
      good: ['Green: well done', 'Task finished clearly faster than planned (e.g. plan 10 h, booked 4 h) or request approved.'],
      note: 'Notice from {{tol}} h deviation per week. Your target hours are visible only to you, HR and management.',
    },
    s8: {
      title: 'Plan, actual and plan/actual ratio',
      lead: 'If you finish a task faster than planned, you can see it clearly.',
      example: { title: 'Example: #6 drinking water riser diagram', plan: 'Plan', booked: 'Booked', result: 'Plan/actual ratio 10 h / 4 h = 250 %' },
      points: [
        'The **plan** is entered by the project lead under **Planning** (only in Projektzeit, not in Forma). Plan 0 = the task is not evaluated.',
        'A task counts once it is **done**. Your own figures show only tasks that you alone booked on; tasks worked on together are evaluated by management only, because otherwise the ratio would reveal the hours of the others.',
        'The basis is always the **recorded** hours. Invoiced hours (the invoice to the client) do not change the ratio.',
        '**My key figures** also show where your time went: by project and by area (project work, training, R&D …). Visible only to you.',
      ],
    },
    s11: {
      title: 'Symbols',
      under: 'Week below target: more than {{tol}} h less booked than your target hours.',
      over: 'Week above target: more than {{tol}} h extra hours.',
      fast: 'Faster than planned.',
      forma: 'Deleted in Forma or no longer visible: bookings stay valid, the task is marked. If the issue is restored in Forma, the mark disappears with the next sync.',
      location: 'Location from Forma, e.g. "Ward building / 2nd floor".',
      hatch: 'Hatching: public holiday, absence or time off in lieu.',
    },
    s12: {
      title: 'Help',
      items: [
        { title: 'Forgot access or password', text: 'The administration sends a new setup link.' },
        { title: 'Made a mistake', text: 'Undo in the message. Entries can be changed freely until approval; the history shows every step.' },
        { title: 'Booked on the wrong task', text: 'Edit the entry and choose "Other task" as long as it is not approved.' },
      ],
    },
  },

  tr: {
    print: 'Yazdır / PDF',
    privacy: {
      title: 'Saatlerimi kim görür?',
      text: 'Taslakları yalnızca siz görürsünüz. Gönderilmiş kayıtlarınızı siz, ilgili projenin proje lideri, ofis (onay) ve yönetim görür. Diğer çalışanlar ne saatlerinizi ne de hedef saatinizi görür; ekip takviminde yalnızca ne zaman ofis dışında olduğunuzu görürler, hastalıkta nedeni görünmez. Hastalık belgelerini yalnızca personel birimi görür. Kişisel göstergeleri yalnızca yönetim görür; her görüntüleme kayıt altına alınır.',
    },
    s1: {
      title: 'Bir bakışta',
      lead: "Görevler Forma'dan gelir, saatler Projektzeit'ta girilir. Forma'da hiçbir şey değiştirilmez.",
      steps: [
        { title: 'Forma', text: "Issue'lar, kategoriler, konumlar ve seçilen alanlar: 15 dakikada bir okunur, her gün 06-24.", who: 'otomatik' },
        { title: 'Kaydet', text: "Saatler bir issue'ya veya manuel göreve girilir; bir issue'ya birden fazla kişi.", who: 'herkes' },
        { title: 'Onayla', text: 'Projeye göre: proje lideri, ofis veya sırayla her ikisi.', who: 'PL, ofis' },
        { title: 'Planla', text: 'Görev başına plan saatleri, haftalık personel.', who: 'PL, yönetim' },
        { title: 'Değerlendir, faturalandır', text: 'Kokpit, proje analizi, Stundennachweis, Abschlag.', who: 'yönetim, faturalandırma' },
      ],
    },
    s2: {
      title: 'Dört adımda zaman kaydı',
      lead: '"Saat gir" düğmesi Genel bakışta, Haftamda veya her görevde bulunur.',
      steps: [
        { title: '1. Aç', text: '"Saat gir" veya Haftamda bir güne tıklayın.' },
        { title: '2. Filtrele', text: 'Kaynak (Forma / kendi), proje, Forma kategori ve tipi, konum.' },
        { title: '3. Görevi bul', text: 'Numara (#12, M-3) veya başlık yazın; son kullanılanlar en üstte.' },
        { title: '4. Gir', text: 'Saat (7,5 veya 7:30), açıklama, çalışma yeri ve faturalama türü.' },
      ],
      billing: '**Faturalama türü**: ön değer görevden ve projeden gelir. Müşteriye faturalanmayacak işi (ör. görevle ilgili iç toplantı, Dynamo betiği gibi kendi geliştirme çalışması) "Faturalandırılmaz" olarak seçin; sabit bir kural (ör. iç görevler) değiştirilemez. Faturalanacak saatleri faturalama yetkilisi belirler. Açıklamayı kendi dilinizde yazabilirsiniz: Stundennachweis\'ta kısaltmanızın (varsayılan ayar) yanında bir cümle olur, açıklama yoksa görev başlığı yazılır; müşteri için metni ofis kontrol eder.',
      submit: 'Cuma günü: **Haftayı gönder**. O zamana kadar kayıtlar taslaktır ve serbestçe değiştirilebilir. **Saatlerim** sayfasında tüm kayıtlarınız listelenir (Bu hafta, Bu ay …); **Excel** bunları yazdığınız iş açıklamasıyla birlikte indirir, kendi yedeğiniz olarak da kullanabilirsiniz.',
      undo: 'Yanlışlıkla mı gönderdiniz veya sildiniz? Sağ alttaki mesajda **Geri al**, ya da henüz onaylanmadıysa Haftamda **Haftayı geri çek**. Her adım kaydın geçmişinde görünür.',
    },
    s3: {
      title: 'Bunu nereye kaydederim?',
      q1: ["Forma'da bir issue var mı?", 'ör. #12 Isı yükü hesabı'],
      a1: ["Forma issue'suna kaydet", 'Kaynak "Forma", proje ve konum seç'],
      q2: ['İş bir projeye mi ait?', 'toplantı, şantiye, ek iş …'],
      a2: ['Projede manuel görev (M numarası)', 'Kategori seç, ör. Toplantı ve koordinasyon'],
      a3: ['"Intern" projesinde iç görev', 'eğitim, Ar-Ge, idari işler … (asla faturalanmaz)'],
      yes: 'evet',
      no: 'hayır',
      aria: "Karar: Forma'da issue varsa ona kaydet. Yoksa proje işi projede manuel görev olarak, değilse iç görev olarak.",
    },
    s4: {
      title: 'Bir zaman kaydının yolu',
      lead: 'Onaydan sonra yalnızca proje lideri veya ofis, gerekçe yazarak değiştirebilir.',
      draft: ['Taslak', 'hâlâ değiştirilebilir'],
      submitted: ['Gönderildi', 'kontrol bekliyor'],
      l1: ['Proje lideri onayladı', '1. aşama tamam'],
      approved: ['Onaylandı', '2. aşama (ofis)'],
      billed: ['Faturalandı', "Stundennachweis'ta"],
      rejected: ['Reddedildi', 'gerekçeli, lütfen düzeltin'],
      arrows: { submit: 'Gönder', lead: 'PL kontrol', office: 'Ofis', close: 'Faturalama', fix: 'düzelt ve yeniden gönder' },
      aria: 'Bir zaman kaydının durum yolu: taslak, gönderildi, proje lideri onayladı, onaylandı, faturalandı; ret düzeltmeye yol açar.',
    },
    s5: {
      title: 'Haftalık düzen',
      days: [
        { d: 'Pzt-Perş', t: 'Her gün kaydet', s: 'en iyisi gün sonunda' },
        { d: 'Cuma', t: 'Haftayı gönder', s: 'Haftamda "Haftayı gönder"' },
        { d: 'Pzt / Salı', t: 'Proje lideri kontrol eder', s: 'Onaylar, 1. aşama' },
        { d: 'Çarş. kadar', t: 'Ofis onaylar', s: 'Onaylar, 2. aşama' },
        { d: 'Ay sonu', t: 'Ay kapanışı', s: 'ardından faturalama' },
      ],
      weekend: '**Hafta sonu** normalde tatildir. Yine de çalışırsanız (ör. teslim öncesi) Haftamda hafta sonunu açıp oraya kaydedin; resmî tatilde, devamsızlık gününde ve hedefin üzerinde çalışılan saatler de fazla mesai sayılır. Fazla mesai **fazla mesai izni** olarak kullanılır veya maaşla **ödenir**; bakiye ve ödemeler Devamsızlıklarım sayfasında görünür.',
    },
    s6: {
      title: 'Devamsızlık, ekip ve resmi tatiller',
      lead: 'Talep edin, belge ekleyin, karara bağlatın ve takvimde görün.',
      items: [
        {
          title: 'İzin ve diğer devamsızlıklar',
          text: '**Devamsızlıklarım** sayfasında talep edin, yarım gün (öğleden önce veya sonra) veya saat olarak da; hakkınız, kalanınız ve fazla mesainiz de orada görünür. Her talep onaylanır veya gerekçeyle reddedilir: onaylayan kişiniz (ör. ekip lideri) veya personel birimi tarafından. Yalnızca onaylanan günler sayılır ve Outlook\'ta görünür. Belge (ör. eğitim daveti) eklenebilir. Başlangıca kadar geri çekebilirsiniz.',
        },
        {
          title: 'Hastalık',
          text: '**Hastalık bildir** ile girin: hedef saat ve takvim için hemen sayılır, personel birimi onaylar. Türünü yalnızca siz ve personel birimi görür, diğer herkes "Ofis dışında" görür.',
        },
        {
          title: 'Ekip ve resmi tatiller',
          text: '**Ekip** sayfasında herkes kimin ne zaman burada olduğunu, her konumun resmi tatillerini, firma etkinliklerini ve kimin hangi projelerde çalıştığını görür; saat ve görev görünmez. Doğum günleri yalnızca kişi profilinde açarsa görünür.',
        },
      ],
      sickDoc: '**{{docDay}}. takvim gününden** itibaren raporu PDF veya fotoğraf olarak yükleyin; hastalık bildiriminde hemen ya da sonradan Devamsızlıklarım altında.',
      change: '**Değişiklik mi isteniyor?** Talep olduğu gibi onaylanamıyorsa onaylayan kişi not ekleyerek **değişiklik talebi** gönderir: günler yeniden serbest kalır, Devamsızlıklarım altında **Düzenle ve yeniden gönder** seçilir; talep önceden doldurulmuş gelir.',
    },
    s7: {
      title: 'Genel bakıştaki uyarılar',
      lead: 'Üç renk, üç anlam.',
      bad: ['Kırmızı: harekete geçin', 'Reddedilen kayıtları düzeltin, reddedilen talepleri inceleyin, eksik raporu yükleyin. Hafta hedef saatinizin belirgin altında: eksik kayıt veya devamsızlık mı var?'],
      warn: ['Sarı: kontrol edin', 'Geçen haftanın kayıtları henüz gönderilmedi. Hafta hedef saatinizin belirgin üstünde (fazla mesai). Onaylayanlar için: açık izin talepleri.'],
      good: ['Yeşil: tebrikler', 'Görev plandan belirgin hızlı bitti (ör. plan 10 sa, girilen 4 sa) veya talep onaylandı.'],
      note: 'Haftalık {{tol}} sa üzeri sapmada uyarı. Hedef saatinizi yalnızca siz, personel birimi ve yönetim görür.',
    },
    s8: {
      title: 'Plan, gerçekleşen ve plan/gerçekleşen oranı',
      lead: 'Bir görevi plandan hızlı bitiren bunu açıkça görür.',
      example: { title: 'Örnek: #6 İçme suyu kolon şeması', plan: 'Plan', booked: 'Girilen', result: 'Plan/gerçekleşen oranı 10 sa / 4 sa = %250' },
      points: [
        '**Plan**ı proje lideri **Planlama** sayfasında girer (yalnızca Projektzeit\'ta, Forma\'da değil). Plan 0 = görev değerlendirilmez.',
        'Görev **tamamlandığında** sayılır. Kendi göstergelerinizde yalnızca tek başınıza saat girdiğiniz görevler yer alır; birlikte çalışılan görevleri yalnızca yönetim değerlendirir, çünkü oran aksi halde diğer kişilerin saatlerini ele verirdi.',
        'Temel her zaman **girilen** saatlerdir. Faturalanacak saatler (müşteriye fatura) oranı değiştirmez.',
        '**Göstergelerim** zamanınızı neye ayırdığınızı da gösterir: projeye ve alana göre (proje çalışması, eğitim, Ar-Ge …). Yalnızca siz görürsünüz.',
      ],
    },
    s11: {
      title: 'Semboller',
      under: 'Hedefin altında hafta: hedef saatinizden {{tol}} saatten fazla eksik giriş.',
      over: 'Hedefin üstünde hafta: hedef saatinizi {{tol}} saatten fazla aşan giriş (fazla mesai).',
      fast: 'Plandan hızlı.',
      forma: "Forma'da silinmiş veya artık görünmüyor: kayıtlar geçerli kalır, görev işaretlenir. Issue Forma'da geri yüklenirse işaret bir sonraki eşitlemede kalkar.",
      location: 'Forma\'dan gelen konum, ör. "Yatak binası / 2. kat".',
      hatch: 'Tarama: resmi tatil, devamsızlık veya fazla mesai izni.',
    },
    s12: {
      title: 'Yardım',
      items: [
        { title: 'Erişim veya şifre unutuldu', text: 'Yönetim yeni bir kurulum bağlantısı gönderir.' },
        { title: 'Bir hata yaptım', text: 'Mesajdaki Geri al. Kayıtlar onaya kadar serbestçe değiştirilebilir; geçmiş her adımı gösterir.' },
        { title: 'Yanlış göreve kaydettim', text: 'Onaylanmadığı sürece kaydı düzenleyip "Başka görev" seçin.' },
      ],
    },
  },
};
